import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatCurrency } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useActiveCurrency } from "@/lib/useCurrency";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell,
} from "recharts";
import { Building2 } from "lucide-react";
import DateRangeFilter from "../components/DateRangeFilter";

export default function Analytics() {
  const { user } = useAuth();
  const currency = useActiveCurrency();
  const isCompanyAdmin = user?.role === "company_admin";
  const [scope, setScope] = useState("all"); // "all" or division_id
  const [range, setRange] = useState({ from: null, to: null, preset: null });

  const { data: divisions = [] } = useQuery({
    queryKey: ["divs"],
    queryFn: () => api.get("/divisions").then((r) => r.data),
    enabled: isCompanyAdmin,
  });

  const divisionParam = scope !== "all" ? scope : undefined;
  const { data, isLoading } = useQuery({
    queryKey: ["overview", scope, range.from, range.to],
    queryFn: () => api.get("/analytics/overview", { params: { division_id: divisionParam, date_from: range.from || undefined, date_to: range.to || undefined } }).then((r) => r.data),
  });
  const { data: forecast } = useQuery({
    queryKey: ["forecast", scope, range.from, range.to],
    queryFn: () => api.get("/analytics/forecast", { params: { division_id: divisionParam, date_from: range.from || undefined, date_to: range.to || undefined } }).then((r) => r.data),
  });
  const { data: leadQuestions } = useQuery({
    queryKey: ["lead-questions", scope, range.from, range.to],
    queryFn: () => api.get("/analytics/lead-questions", { params: { division_id: divisionParam, date_from: range.from || undefined, date_to: range.to || undefined, limit: 15 } }).then((r) => r.data),
  });
  const { data: income } = useQuery({
    queryKey: ["income", scope],
    queryFn: () => api.get("/analytics/income", { params: { division_id: divisionParam } }).then((r) => r.data),
  });

  if (isLoading || !data) return <div><PageHeader overline="Metrics" title="Analytics" /><div className="p-10 text-muted-foreground">Loading…</div></div>;

  const funnel = [
    { stage: "Inquiries", value: data.total_inquiries },
    { stage: "Lead Inquiries", value: data.total_lead_inquiries },
    { stage: "Leads Converted", value: data.leads_converted },
    { stage: "Deals Won", value: data.deals_won },
  ];

  const stagesData = Object.entries(data.stage_values || {}).map(([name, v]) => ({ name, value: v.value, count: v.count, is_won: v.is_won, is_lost: v.is_lost }));
  const tagData = Object.entries(data.tag_stats || {}).map(([tag, s]) => ({ tag, total: s.value, won: s.won_value, count: s.count }));

  const scopeLabel = scope === "all" ? (isCompanyAdmin ? "All divisions (company-wide)" : "Your division") : (divisions.find((d) => d.id === scope)?.name || "");

  return (
    <div>
      <PageHeader
        overline="Metrics"
        title="Analytics"
        subtitle={`${scopeLabel} · Multi-stage funnel with tag & source breakdown.`}
        actions={
          isCompanyAdmin && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="analytics-division-switcher" className="rounded-sm w-64 bg-secondary/40">
                <Building2 className="h-3.5 w-3.5 mr-2 text-primary" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions (Company-wide)</SelectItem>
                {divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.currency}</SelectItem>)}
              </SelectContent>
            </Select>
          )
        }
      />

      <div className="px-6 lg:px-10 pb-12 space-y-6">
        {/* Date range filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Time range:</span>
          <DateRangeFilter value={range} onChange={setRange} testId="analytics-date" />
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Kpi overline="Pipeline" label="Potential Value" value={formatCurrency(data.pipeline_value, currency)} highlight />
          <Kpi overline="Won" label="Revenue Closed" value={formatCurrency(data.total_won_value, currency)} />
          <Kpi overline="Avg" label="Deal Value" value={formatCurrency(data.avg_deal_value, currency)} />
          <Kpi overline="Speed" label="Time to Close" value={`${Math.round(data.avg_time_to_close_days || 0)}d`} />
        </div>

        {/* Forecast */}
        {forecast && <ForecastSection forecast={forecast} currency={currency} />}

        {/* Lead-Capture Analytics */}
        {leadQuestions && <LeadQuestionsSection data={leadQuestions} />}

        {/* Monthly Income Dashboard */}
        {income && <IncomeSection data={income} />}

        {/* Conversion ratios */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <Ratio label="Inquiry → Lead" value={data.inquiries_to_leads_ratio} sub={`${data.total_lead_inquiries} / ${data.total_inquiries}`} />
          <Ratio label="Lead → Deal" value={data.leads_to_deals_ratio} sub={`${data.leads_converted} / ${data.total_leads}`} />
          <Ratio label="Deal → Won" value={data.deals_to_close_ratio} sub={`${data.deals_won} / ${data.total_deals}`} />
        </div>

        {/* Funnel chart */}
        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Conversion Funnel</div>
          <div className="font-display text-xl font-extrabold mb-4">From inquiry to closed won</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" />
              <XAxis type="number" stroke="#71717A" />
              <YAxis dataKey="stage" type="category" stroke="#71717A" width={120} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 4 }} />
              <Bar dataKey="value" fill="hsl(211 100% 50%)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Pipeline value by stage */}
        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Pipeline</div>
          <div className="font-display text-xl font-extrabold mb-4">Value by Stage</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stagesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" />
              <XAxis dataKey="name" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 4 }} formatter={(v) => formatCurrency(v, currency)} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {stagesData.map((s, i) => (
                  <Cell key={i} fill={s.is_won ? "#34C759" : s.is_lost ? "#FF3B30" : "hsl(211 100% 50%)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Tag breakdown */}
          <Card className="rounded-sm bg-card border-border p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Categories</div>
            <div className="font-display text-xl font-extrabold mb-4">Deal Value by Tag</div>
            {tagData.length === 0 && <div className="text-sm text-muted-foreground">No tagged deals yet.</div>}
            <div className="space-y-2">
              {tagData.map((t) => (
                <div key={t.tag} className="flex items-center gap-3">
                  <Badge variant="secondary" className="rounded-sm w-24 justify-center">{t.tag}</Badge>
                  <div className="flex-1 bg-secondary/30 h-6 rounded-sm relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-primary/70" style={{ width: `${Math.min(100, (t.total / Math.max(...tagData.map((x) => x.total || 1))) * 100)}%` }} />
                    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                      <span>{t.count} deals</span>
                      <span className="font-bold">{formatCurrency(t.total, currency)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Salesperson performance */}
          <Card className="rounded-sm bg-card border-border p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Team</div>
            <div className="font-display text-xl font-extrabold mb-4">Salesperson Performance</div>
            <div className="space-y-2">
              {(data.agent_performance || []).map((a) => (
                <div key={a.agent_id} className="p-3 border border-border rounded-sm bg-secondary/20">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-sm">{a.name}</div>
                    <div className="text-xs text-primary font-bold">{formatCurrency(a.won_value, currency)}</div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{a.deals} deals · {a.won} won</span>
                    <span>{(a.conversion_rate * 100).toFixed(0)}% win rate</span>
                  </div>
                </div>
              ))}
              {(data.agent_performance || []).length === 0 && <div className="text-sm text-muted-foreground">No agents with deals yet.</div>}
            </div>
          </Card>
        </div>

        {/* Source performance */}
        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Attribution</div>
          <div className="font-display text-xl font-extrabold mb-4">Lead Source Performance</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(data.source_performance || {}).map(([src, s]) => (
              <div key={src} className="p-4 border border-border rounded-sm bg-secondary/20">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{src}</div>
                <div className="font-display text-2xl font-extrabold mt-1">{s.count}</div>
                <div className="text-xs text-primary">{formatCurrency(s.won_value, currency)} won</div>
              </div>
            ))}
            {Object.keys(data.source_performance || {}).length === 0 && <div className="text-sm text-muted-foreground col-span-full">No source data yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ overline, label, value, highlight }) {
  return (
    <Card className={`rounded-sm p-6 border-border ${highlight ? "bg-primary/10 border-primary/40" : "bg-card"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{overline}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
      <div className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{value}</div>
    </Card>
  );
}

function Ratio({ label, value, sub }) {
  const pct = Math.min(1, Math.max(0, value || 0));
  return (
    <Card className="rounded-sm bg-card border-border p-6 relative overflow-hidden">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      <div className="font-display text-4xl font-extrabold tracking-tight mt-2">{(pct * 100).toFixed(0)}%</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
      <div className="absolute inset-x-0 bottom-0 h-1 bg-primary" style={{ width: `${pct * 100}%` }} />
    </Card>
  );
}


function ForecastSection({ forecast, currency }) {
  const stageTable = (forecast.deals_by_stage || []).sort((a, b) => (b.weighted_value || 0) - (a.weighted_value || 0));
  const trend = forecast.monthly_trend || [];
  const byTag = forecast.by_tag || [];
  const winPct = ((forecast.overall_win_rate || 0) * 100).toFixed(1);

  return (
    <Card className="rounded-sm bg-card border-border p-6 relative overflow-hidden" data-testid="forecast-section">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="flex items-start justify-between gap-4 mb-5 relative">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Forecast</div>
          <div className="font-display text-2xl font-extrabold mt-1">Expected Revenue</div>
          <div className="text-xs text-muted-foreground mt-1">
            Weighted by historical win rate at each stage · overall win rate {winPct}% ({forecast.open_deals_count} open deals)
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground text-right">
          As of<br /><span className="text-primary">{new Date(forecast.as_of).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
        </div>
      </div>

      {/* Forecast tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative">
        <ForecastTile testId="forecast-weighted" overline="Weighted" label="Pipeline value" value={formatCurrency(forecast.weighted_pipeline_value, currency)} sub={`Best case: ${formatCurrency(forecast.best_case_pipeline_value, currency)}`} accent="primary" />
        <ForecastTile testId="forecast-this-month" overline="This month" label="Expected to close" value={formatCurrency(forecast.expected_this_month, currency)} sub="Sum of weighted deals with expected close date ≤ month-end" />
        <ForecastTile testId="forecast-90d" overline="Next 90 days" label="Expected revenue" value={formatCurrency(forecast.expected_next_90_days, currency)} sub={`30d: ${formatCurrency(forecast.expected_next_30_days, currency)} · 60d: ${formatCurrency(forecast.expected_next_60_days, currency)}`} />
        <ForecastTile testId="forecast-runrate" overline="Run rate" label="Annualised" value={formatCurrency(forecast.run_rate_annual, currency)} sub={`Trailing 30-day closed-won × 12 · Cycle: ${forecast.median_time_to_close_days || 0}d`} accent="green" />
      </div>

      {/* Trend + stage breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6 relative">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Closed-won trend · last 6 months</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" />
              <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 4 }} formatter={(v) => formatCurrency(v, currency)} />
              <Line type="monotone" dataKey="closed_won_value" stroke="hsl(211 100% 50%)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Weighted value by stage</div>
          <div className="rounded-sm border border-border overflow-hidden">
            <table className="w-full text-xs" data-testid="forecast-stage-table">
              <thead className="bg-secondary/40">
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground text-left">
                  <th className="p-2">Stage</th>
                  <th className="p-2 text-right">Deals</th>
                  <th className="p-2 text-right">Value</th>
                  <th className="p-2 text-right">Prob</th>
                  <th className="p-2 text-right">Weighted</th>
                </tr>
              </thead>
              <tbody>
                {stageTable.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">No open deals</td></tr>}
                {stageTable.map((s) => (
                  <tr key={s.stage_id} className="border-t border-border/60">
                    <td className="p-2 truncate max-w-[140px]">{s.stage_name}</td>
                    <td className="p-2 text-right">{s.count}</td>
                    <td className="p-2 text-right text-muted-foreground">{formatCurrency(s.value, currency)}</td>
                    <td className="p-2 text-right">{Math.round((s.probability || 0) * 100)}%</td>
                    <td className="p-2 text-right font-bold text-primary">{formatCurrency(s.weighted_value, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Tag forecast */}
      {byTag.length > 0 && (
        <div className="mt-6 relative">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Weighted value by tag</div>
          <div className="flex flex-wrap gap-2" data-testid="forecast-tag-chips">
            {byTag.slice(0, 12).map((t) => (
              <div key={t.tag} className="rounded-sm border border-border bg-secondary/40 px-3 py-2 min-w-[140px]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.tag}</div>
                <div className="font-display text-lg font-extrabold text-primary">{formatCurrency(t.weighted_value, currency)}</div>
                <div className="text-[10px] text-muted-foreground">{t.count} deals · {formatCurrency(t.value, currency)} best-case</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function ForecastTile({ overline, label, value, sub, accent, testId }) {
  const accentClass = accent === "green" ? "border-l-green-500" : accent === "primary" ? "border-l-primary" : "border-l-border";
  return (
    <div data-testid={testId} className={`bg-secondary/40 border border-border ${accentClass} border-l-2 rounded-sm p-4`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{overline}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
      <div className="font-display text-xl lg:text-2xl font-extrabold tracking-tight mt-2 break-all">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1.5 leading-tight">{sub}</div>
    </div>
  );
}


const CHANNEL_LABEL = {
  chat: "Web chat",
  whatsapp: "WhatsApp",
  email: "Email",
  facebook: "Facebook",
  instagram: "Instagram",
  unknown: "Other",
};
const CHANNEL_COLOR = {
  chat: "#22c55e",
  whatsapp: "#25D366",
  email: "#38BDF8",
  facebook: "#4267B2",
  instagram: "#E1306C",
  unknown: "#94A3B8",
};

function LeadQuestionsSection({ data }) {
  const questions = data.top_questions || [];
  const byChannel = Object.entries(data.by_channel || {}).sort((a, b) => b[1] - a[1]);
  const total = data.total_leads || 0;
  const maxCount = Math.max(1, ...questions.map((q) => q.count));

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="lead-questions-section">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Lead-Capture Signals</div>
          <div className="font-display text-2xl font-extrabold">Top questions before a lead</div>
          <div className="text-sm text-muted-foreground mt-1">
            The first message visitors sent on conversations that became qualified leads. Great input for your FAQ + knowledge base.
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Leads in range</div>
          <div className="font-display text-3xl font-black text-primary">{total}</div>
        </div>
      </div>

      {byChannel.length > 0 && (
        <div className="mb-6" data-testid="lead-questions-by-channel">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">By channel</div>
          <div className="flex flex-wrap gap-2">
            {byChannel.map(([ch, count]) => (
              <div
                key={ch}
                className="rounded-sm border border-border bg-secondary/30 px-3 py-1.5 text-xs flex items-center gap-2"
                data-testid={`lead-channel-${ch}`}
              >
                <span className="h-2 w-2 rounded-full inline-block" style={{ background: CHANNEL_COLOR[ch] || CHANNEL_COLOR.unknown }} />
                <span className="font-semibold">{CHANNEL_LABEL[ch] || ch}</span>
                <span className="text-muted-foreground">· {count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {questions.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-10">
          No lead-flagged conversations in the current date range yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="lead-questions-list">
          {questions.map((q, i) => {
            const pct = (q.count / maxCount) * 100;
            return (
              <div key={i} className="rounded-sm border border-border bg-secondary/20 p-3" data-testid={`lead-question-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm flex-1 min-w-0 truncate" title={q.question}>{q.question}</div>
                  <div className="text-xs font-bold text-primary flex-none">×{q.count}</div>
                </div>
                <div className="mt-2 h-1 rounded-sm bg-secondary/60 overflow-hidden">
                  <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  {(q.channels || []).map((ch) => (
                    <span
                      key={ch}
                      className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
                      style={{ background: (CHANNEL_COLOR[ch] || "#94A3B8") + "22", color: CHANNEL_COLOR[ch] || "#94A3B8" }}
                    >
                      {CHANNEL_LABEL[ch] || ch}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtSGD = (n) => new Intl.NumberFormat(undefined, { style: "currency", currency: "SGD", maximumFractionDigits: 0 }).format(n || 0);

function IncomeSection({ data }) {
  const months = data.months || [];
  const totals = data.totals || {};
  const byDiv = data.by_division || [];
  const trends = data.trends || {};
  const maxMonthly = Math.max(1, ...months.map((m) => Math.max(m.invoiced, m.paid)));

  const kpi = (label, key, tone) => (
    <IncomeStat
      label={label}
      value={fmtSGD(totals[key])}
      tone={tone}
      wow={trends?.wow?.[key]}
      mom={trends?.mom?.[key]}
      // "Overdue up" is bad, so flip the tone for that KPI's arrow colour
      arrowInverted={key === "outstanding" || key === "overdue"}
      testId={`income-kpi-${key}`}
    />
  );

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="income-section">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Cash Flow</div>
          <div className="font-display text-2xl font-extrabold">Monthly income</div>
          <div className="text-sm text-muted-foreground mt-1">Paid vs outstanding invoices for {data.year}, broken down per division. Arrows compare last 7 days (WoW) and month-to-date (MoM).</div>
        </div>
        <div className="flex flex-wrap gap-3">
          {kpi("Invoiced", "invoiced", "neutral")}
          {kpi("Received", "paid", "green")}
          {kpi("Outstanding", "outstanding", "primary")}
          {kpi("Overdue", "overdue", "red")}
        </div>
      </div>

      {/* Monthly bars */}
      <div className="mb-8" data-testid="income-months">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-3">By month</div>
        <div className="grid grid-cols-12 gap-2 items-end h-40">
          {months.map((m) => {
            const i = parseInt(m.month.split("-")[1], 10) - 1;
            const paidPct = maxMonthly > 0 ? (m.paid / maxMonthly) * 100 : 0;
            const outstandingPct = maxMonthly > 0 ? (m.outstanding / maxMonthly) * 100 : 0;
            return (
              <div key={m.month} className="flex flex-col items-center justify-end gap-0.5 h-full" data-testid={`income-month-${m.month}`} title={`${MONTH_LABELS[i]}: paid ${fmtSGD(m.paid)} · outstanding ${fmtSGD(m.outstanding)}`}>
                <div className="flex flex-col-reverse w-full h-full justify-start gap-0.5">
                  <div className="w-full bg-green-500/70 rounded-t-sm transition-all" style={{ height: `${paidPct}%` }} />
                  <div className="w-full bg-primary/60 rounded-sm transition-all" style={{ height: `${outstandingPct}%` }} />
                </div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{MONTH_LABELS[i]}</div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="flex items-center gap-1.5"><span className="h-2 w-3 bg-green-500/70 rounded-sm inline-block" /> Received</div>
          <div className="flex items-center gap-1.5"><span className="h-2 w-3 bg-primary/60 rounded-sm inline-block" /> Outstanding</div>
        </div>
      </div>

      {/* By division */}
      {byDiv.length > 0 && (
        <div data-testid="income-by-division">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-3">By division</div>
          <div className="space-y-2">
            {byDiv.map((d) => {
              const totalD = d.paid + d.outstanding;
              const paidPct = totalD > 0 ? (d.paid / totalD) * 100 : 0;
              return (
                <div key={d.division_id} className="rounded-sm border border-border bg-secondary/20 p-3" data-testid={`income-div-${d.division_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm">{d.division_name}</div>
                    <div className="text-xs text-muted-foreground">{d.invoice_count} invoice{d.invoice_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="h-2 rounded-sm bg-secondary/60 overflow-hidden flex">
                    <div className="h-full bg-green-500/70" style={{ width: `${paidPct}%` }} />
                    <div className="h-full bg-primary/60" style={{ width: `${100 - paidPct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-green-400">Received {fmtSGD(d.paid)}</span>
                    <span className="text-primary">Outstanding {fmtSGD(d.outstanding)}</span>
                    {d.overdue > 0 && <span className="text-red-400">Overdue {fmtSGD(d.overdue)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function IncomeStat({ label, value, tone, wow, mom, arrowInverted = false, testId }) {
  const cls = { neutral: "text-foreground", primary: "text-primary", green: "text-green-400", red: "text-red-400" }[tone] || "";
  return (
    <div className="rounded-sm border border-border bg-secondary/30 px-3 py-2 min-w-[140px]" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-lg font-black ${cls}`}>{value}</div>
      <div className="mt-1 flex items-center gap-2 text-[10px] font-semibold">
        <TrendArrow label="WoW" pct={wow?.pct} inverted={arrowInverted} testId={testId ? `${testId}-wow` : undefined} />
        <TrendArrow label="MoM" pct={mom?.pct} inverted={arrowInverted} testId={testId ? `${testId}-mom` : undefined} />
      </div>
    </div>
  );
}

function TrendArrow({ label, pct, inverted = false, testId }) {
  if (pct == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground" data-testid={testId}>
        <span className="text-[8px] uppercase tracking-widest">{label}</span>
        <span>—</span>
      </span>
    );
  }
  const isUp = pct > 0;
  const isDown = pct < 0;
  // For "bad-when-up" metrics (outstanding, overdue), invert the colour meaning
  const good = inverted ? isDown : isUp;
  const bad = inverted ? isUp : isDown;
  const color = good ? "text-green-400" : bad ? "text-red-400" : "text-muted-foreground";
  const arrow = isUp ? "▲" : isDown ? "▼" : "•";
  return (
    <span className={`inline-flex items-center gap-0.5 ${color}`} data-testid={testId}>
      <span className="text-[8px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span aria-hidden>{arrow}</span>
      <span>{isUp ? "+" : ""}{pct}%</span>
    </span>
  );
}

